import { useState, useEffect, useRef, useCallback } from 'react'
import type * as Y from 'yjs'

/**
 * Shared Y.Text → React string binding for the text editors (Markdown, Html,
 * Script). Returns the current text, debounced via rAF to avoid thrash during
 * bulk sync.
 *
 * Reload-aware: when `reloading` is true (a reconnect forced a fresh Y.Doc and
 * it hasn't resynced yet), the initial *empty* paint of the new doc is
 * suppressed so the editor keeps showing the last content instead of flashing
 * empty (the cache→empty→repaint blip). The observer still fires when the
 * resync fills the doc, swapping in the new content. Centralizing this here is
 * what keeps the no-blip behaviour from being re-implemented (and drifting) in
 * every text editor.
 */
export function useCollabText(ytext: Y.Text | null, reloading: boolean): string {
  const [content, setContent] = useState('')
  // Read `reloading` through a ref so the observer effect keys ONLY on `ytext`.
  // Keeping `reloading` out of the dep array avoids tearing down + re-attaching
  // the Y.Text observer on every reconnect (true→false) flip — churn that also
  // opened a brief window where an update could land while no observer was
  // attached. The flip only gates the one-time initial paint below, which is
  // re-evaluated whenever `ytext` itself changes (the doc-rebuild case).
  const reloadingRef = useRef(reloading)
  reloadingRef.current = reloading
  useEffect(() => {
    if (!ytext) return
    let rafId: number | null = null
    const observer = () => {
      if (rafId !== null) return // already scheduled — skip duplicate
      rafId = requestAnimationFrame(() => {
        rafId = null
        setContent(ytext.toString())
      })
    }
    ytext.observe(observer)
    if (!(reloadingRef.current && ytext.length === 0)) setContent(ytext.toString())
    return () => {
      ytext.unobserve(observer)
      if (rafId !== null) cancelAnimationFrame(rafId)
    }
  }, [ytext])

  // When a reload finishes (reloading true→false), force a re-read of the
  // resynced doc. The observer only fires on an actual Y.Text delta, so if the
  // resync produced NO delta relative to the suppressed-stale content — e.g.
  // another client emptied the file during the reconnect, so the fresh doc
  // resyncs to empty — the stale content would otherwise stay painted. Separate
  // effect (not in the observer's deps) so it doesn't re-subscribe the observer.
  useEffect(() => {
    if (!reloading && ytext) setContent(ytext.toString())
  }, [reloading, ytext])

  return content
}

/**
 * The text the editor should show NOW, for TextModifier's `readLatestValue`: the live Y.Text,
 * or — while a reload holds the last content (see above) — that held content. `content` alone
 * is a frame old, and painting it over newer keystrokes drops them (#1218's e2e).
 */
export function useLatestCollabText(ytext: Y.Text | null, reloading: boolean, content: string): () => string {
  const contentRef = useRef(content)
  contentRef.current = content
  const reloadingRef = useRef(reloading)
  reloadingRef.current = reloading
  return useCallback(
    () => (ytext && !reloadingRef.current ? ytext.toString() : contentRef.current),
    [ytext],
  )
}
